import { useCallback, useEffect, useMemo } from 'react';
import useNRLazyQuery from '../../../../graphql/useNRLazyQuery';
import useNRQuery from '../../../../graphql/useNRQuery';
import {
  GET_JIRA_INTEGRATION,
  GET_JIRA_SPACE_ISSUE_FIELDS,
  GET_JIRA_SPACE_ISSUE_TYPES
} from '../../../../graphql/queries/jiraIntegration';
import { transformJiraIntegrationConnectionHealth } from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/jiraIntegration.utils';
import { JIRA_ASSIGNEE_FIELD_KEY, JIRA_PRIORITY_FIELD_KEY } from './createJiraTicket.constants';
import {
  getJiraFieldByKey,
  getPriorityOptionsFromFields,
  getRequiredRpcOptionSources,
  shouldShowPriorityField
} from './createJiraTicket.utils';

export function useJiraCreateTicketSchema({ spaceId, issueTypeId }) {
  const [jiraIntegration] = useNRQuery({
    query: GET_JIRA_INTEGRATION,
    qlObjKey: 'jiraIntegration',
    transformData: transformJiraIntegrationConnectionHealth,
    defaultValue: null
  });

  const [issueTypes, issueTypesLoading, , fetchIssueTypes] = useNRLazyQuery({
    query: GET_JIRA_SPACE_ISSUE_TYPES,
    qlObjKey: 'jiraSpaceIssueTypes',
    defaultValue: []
  });

  const [issueFields, issueFieldsLoading, issueFieldsCalled, fetchIssueFields] = useNRLazyQuery({
    query: GET_JIRA_SPACE_ISSUE_FIELDS,
    qlObjKey: 'jiraSpaceIssueFields',
    defaultValue: []
  });

  const spaceOptions = useMemo(
    () =>
      (jiraIntegration?.connectedSpaces ?? []).map(space => ({
        value: space.id,
        label: space.name,
        text: space.name
      })),
    [jiraIntegration?.connectedSpaces]
  );

  const issueTypeOptions = useMemo(
    () =>
      (issueTypes ?? [])
        .filter(issueType => !issueType.subtask)
        .map(issueType => ({
          value: issueType.id,
          label: issueType.name,
          text: issueType.name
        })),
    [issueTypes]
  );

  const priorityField = useMemo(() => getJiraFieldByKey(issueFields, JIRA_PRIORITY_FIELD_KEY), [issueFields]);
  const assigneeField = useMemo(() => getJiraFieldByKey(issueFields, JIRA_ASSIGNEE_FIELD_KEY), [issueFields]);
  const requiredRpcOptionSources = useMemo(() => getRequiredRpcOptionSources(issueFields), [issueFields]);
  const showPriorityField = useMemo(() => shouldShowPriorityField(issueFields), [issueFields]);
  const priorityOptions = useMemo(() => getPriorityOptionsFromFields(issueFields), [issueFields]);

  useEffect(() => {
    if (!spaceId) {
      return;
    }

    fetchIssueTypes({ variables: { args: { spaceId } } });
  }, [fetchIssueTypes, spaceId]);

  useEffect(() => {
    if (!spaceId || !issueTypeId) {
      return;
    }

    fetchIssueFields({
      variables: {
        args: {
          spaceId,
          issueTypeId
        }
      }
    });
  }, [fetchIssueFields, issueTypeId, spaceId]);

  const refreshIssueFields = useCallback(() => {
    if (!spaceId || !issueTypeId) {
      return;
    }

    fetchIssueFields({
      fetchPolicy: 'network-only',
      variables: {
        args: {
          spaceId,
          issueTypeId
        }
      }
    });
  }, [fetchIssueFields, issueTypeId, spaceId]);

  return {
    spaceOptions,
    issueTypeOptions,
    issueTypesLoading,
    issueFields,
    issueFieldsLoading,
    issueFieldsCalled,
    priorityField,
    assigneeField,
    requiredRpcOptionSources,
    showPriorityField,
    priorityOptions,
    refreshIssueFields
  };
}
